import { API } from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import { OutboundAllowlistInterface, OutboundAllowlistKind, OutboundAllowlistPreview, OutboundGateStatus } from "@swivl-mono/types";
import { Button, ButtonColor, ButtonSize, ButtonVariant, Input, Select, SelectItem } from "@swivl-mono/ui";
import { useCallback, useEffect, useState } from "react";

import { NavBar } from "../../../Models/NavBar/NavBar.Model";
import { AlertsModel } from "../../../Models/Notifications/Alerts.Model";

const KIND_OPTIONS: SelectItem[] = [
  { value: "phone", label: "Phone (texts + calls)" },
  { value: "email", label: "Email" },
  { value: "memberId", label: "Member id (push)" },
  { value: "tenant", label: "Tenant (FMS writes on that record)" }
];

const KIND_LABELS: Record<OutboundAllowlistKind, string> = {
  phone: "Phone",
  email: "Email",
  memberId: "Member id",
  tenant: "Tenant (FMS writes)"
};

const PLACEHOLDERS: Record<OutboundAllowlistKind, string> = {
  phone: "+14385551234",
  email: "qa@example.com",
  memberId: "42",
  tenant: "Swivl tenant id, e.g. 9112714"
};

/** Kinds that name a record: the page shows whose it is before the entry can be added. */
const PREVIEW_KINDS: ReadonlySet<OutboundAllowlistKind> = new Set<OutboundAllowlistKind>(["tenant"]);
const PREVIEW_DEBOUNCE_MS = 400;

/**
 * Swivl-admin page for the Outbound Allowlist (ENG-3373, the Outbound Gate). The list is the
 * fine-grained "who" outside production: with an org's stream flag on, only listed recipients get
 * real sends there; everyone else is simulated. In production the org's flags alone decide and the
 * list is not consulted. Changes reach every server within a minute. A tenant entry shows whose
 * record it is before it can be added (2026-09-22).
 */
export function OutboundAllowlistAdminView(): JSX.Element {
  const [rows, setRows] = useState<OutboundAllowlistInterface[]>([]);
  const [status, setStatus] = useState<OutboundGateStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState<OutboundAllowlistKind>("phone");
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [preview, setPreview] = useState<OutboundAllowlistPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    NavBar.set(<NavBar.Component.Title title="Admin" />, null, null, "Admin");
  }, []);

  const load = useCallback(async (): Promise<void> => {
    try {
      const [list, gateStatus] = await Promise.all([API.OutboundAllowlist.list(), API.OutboundAllowlist.status()]);
      setRows(list);
      setStatus(gateStatus);
    } catch {
      AlertsModel.errorToast("Could not load the outbound allowlist");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Who is this? For tenant and org entries the answer appears under the value box before Add is
  // enabled, so nobody allowlists a customer's record by mistake.
  useEffect(() => {
    setPreview(null);
    setPreviewError(null);
    if (!PREVIEW_KINDS.has(kind) || !value.trim()) return;
    setPreviewing(true);
    const timer = setTimeout(async () => {
      try {
        setPreview(await API.OutboundAllowlist.preview(kind, value.trim()));
      } catch (err) {
        setPreviewError(err instanceof Error && err.message ? err.message : "Could not look that up");
      } finally {
        setPreviewing(false);
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [kind, value]);

  const needsPreview = PREVIEW_KINDS.has(kind);
  const canAdd = !saving && !!value.trim() && (!needsPreview || (preview !== null && !previewing));

  const handleAdd = async (): Promise<void> => {
    setSaving(true);
    try {
      const added = await API.OutboundAllowlist.add({ kind, value, note: note.trim() || undefined });
      setRows((prev) => [...prev, added].sort((a, b) => a.kind.localeCompare(b.kind) || a.value.localeCompare(b.value)));
      setValue("");
      setNote("");
      setPreview(null);
    } catch (err) {
      AlertsModel.errorToast(err instanceof Error && err.message ? err.message : "Could not add that entry");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (row: OutboundAllowlistInterface): Promise<void> => {
    setRemovingId(row.id);
    try {
      await API.OutboundAllowlist.remove(row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch {
      AlertsModel.errorToast(`Could not remove ${row.value}`);
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="p-6">
      <h1 className="heading-lg mb-1">Outbound Allowlist</h1>
      <p className="mb-4 max-w-3xl text-sm text-grey-600">
        Who Swivl may actually reach. Every text, call, email, push and FMS write to anyone <span className="font-semibold">not</span> on this list is{" "}
        <span className="font-semibold">simulated</span> — built and recorded, never sent — in every environment, production included. A{" "}
        <span className="font-semibold">tenant</span> entry allows FMS writes on that one tenant's record; an{" "}
        <span className="font-semibold">org</span> entry allows every recipient of a launched customer, in production only. Edits take effect on all
        servers within a minute.
      </p>

      {status && <GateStatusBanner status={status} />}

      <div className="mb-6 flex flex-wrap items-end gap-3 rounded border border-grey-200 p-4">
        <div className="w-56">
          <label className="mb-1 block text-xs font-semibold text-grey-600 uppercase">Kind</label>
          <Select options={KIND_OPTIONS} selectedValues={[kind]} hideClear onChange={(vals) => setKind(vals[0] as OutboundAllowlistKind)} />
        </div>
        <div className="w-64">
          <label className="mb-1 block text-xs font-semibold text-grey-600 uppercase">Value</label>
          <Input value={value} placeholder={PLACEHOLDERS[kind]} onChange={(e) => setValue(e.target.value)} />
        </div>
        <div className="w-72">
          <label className="mb-1 block text-xs font-semibold text-grey-600 uppercase">Note (optional)</label>
          <Input value={note} placeholder="Whose number / what it's for" onChange={(e) => setNote(e.target.value)} />
        </div>
        <Button color={ButtonColor.ORANGE} size={ButtonSize.MEDIUM} variant={ButtonVariant.PRIMARY} disabled={!canAdd} onClick={handleAdd}>
          {saving ? "Adding…" : "Add"}
        </Button>
        {needsPreview && value.trim() && (
          <div className="basis-full text-sm">
            {previewing && <span className="text-grey-500">Looking up who this is…</span>}
            {!previewing && preview && (
              <span className="text-grey-900">
                This allows <span className="font-semibold">{preview.label}</span> — {preview.detail}
              </span>
            )}
            {!previewing && previewError && <span className="text-red-700">{previewError}</span>}
          </div>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-grey-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-grey-500">The allowlist is empty — every outbound action, everywhere, is simulated.</p>
      ) : (
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-grey-200 text-left text-xs tracking-wide text-grey-600 uppercase">
              <th className="py-2 pr-4">Kind</th>
              <th className="py-2 pr-4">Value</th>
              <th className="py-2 pr-4">Note</th>
              <th className="py-2 pr-4">Added</th>
              <th className="py-2 pr-4" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-b border-grey-100">
                <td className="py-2 pr-4">{KIND_LABELS[row.kind]}</td>
                <td className="py-2 pr-4 font-mono">{row.value}</td>
                <td className="py-2 pr-4 text-grey-600">{row.note ?? ""}</td>
                <td className="py-2 pr-4 text-grey-500">
                  {new Date(row.createdAt).toLocaleDateString()}
                  {row.createdByMemberId !== null && <span className="ml-1 text-xs">by member {row.createdByMemberId}</span>}
                </td>
                <td className="py-2 pr-4 text-right">
                  <Button
                    color={ButtonColor.RED}
                    size={ButtonSize.SMALL}
                    variant={ButtonVariant.GHOST}
                    disabled={removingId === row.id}
                    onClick={() => handleRemove(row)}
                  >
                    {removingId === row.id ? "Removing…" : "Remove"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

interface GateStatusBannerProps {
  status: OutboundGateStatus;
}

/** Where this server is, and whether it has the list, so an admin can see at a glance what a send would do from here. */
function GateStatusBanner({ status }: GateStatusBannerProps): JSX.Element {
  const production = status.environment === "production";
  return (
    <div className={filterClasses("mb-4 rounded border p-3 text-sm", production ? "border-yellow-300 bg-yellow-50" : "border-grey-200 bg-grey-50")}>
      <div className="font-semibold">
        This server is <span className="font-mono">{status.environment}</span>
        {production ? " — the org flags alone decide; this list is not consulted here." : " — the org flags AND this list decide."}
      </div>
      <div className="text-grey-600">
        Allowlist: {status.allowlistLoaded ? "loaded on this server." : "not yet loaded on this server — everything is simulated until it is."}
      </div>
    </div>
  );
}
